import React, { useState, useEffect } from 'react';
import { CreditCard, CheckCircle2, History, AlertCircle, FileText } from 'lucide-react';
import axiosClient from '../api/axiosClient';
import ClientDashboardAnalytics from '../components/client/ClientDashboardAnalytics';

export default function ClientSubscriptions() {
  const [ledger, setLedger] = useState([]);
  const [balance, setBalance] = useState(0);
  const [currentPlan, setCurrentPlan] = useState('Loading...');
  const [loading, setLoading] = useState(true);
  const [payAmount, setPayAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [profile, setProfile] = useState({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchLedger();
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axiosClient.get('/trials/me');
      setProfile(res.data.profile || {});
    } catch (err) {
      console.error(err);
    }
  };

  const fetchLedger = async () => {
    try {
      const res = await axiosClient.get('/trials/me/ledger');
      setLedger(res.data.ledger);
      setBalance(res.data.balance);
      setCurrentPlan(res.data.currentPlan);
    } catch (err) {
      console.error('Failed to load ledger', err);
      setCurrentPlan('Unknown');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!payAmount || Number(payAmount) <= 0) return;
    
    setProcessing(true);
    try {
      // Simulate/trigger payment via checkout route
      await axiosClient.post('/plans/checkout', {
        name: profile.name || 'Client',
        email: profile.email || 'client@example.com',
        schoolName: profile.school_name || 'School',
        planName: currentPlan,
        studentsCount: 0, // Since this is a custom partial payment
        paymentMode: balance === Number(payAmount) ? 'full' : 'partial',
        amountPaid: Number(payAmount)
      });
      
      setSuccess(true);
      setPayAmount('');
      fetchLedger();
      
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Payment failed', err);
      alert('Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Subscriptions & Payments</h1>
            <p className="text-slate-600">Manage your active plans, view ledger history, and make payments.</p>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-500 bg-white rounded-2xl border border-slate-200">
            Loading your billing information...
          </div>
        ) : (
          <div className="space-y-6">
            <ClientDashboardAnalytics />
            
            {/* Top Cards */}
            <div className="grid md:grid-cols-2 gap-6">
              
              {/* Active Plan Card */}
              <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 text-slate-500 font-medium mb-4">
                    <CheckCircle2 size={18} className="text-primary-600" />
                    Active Plan
                  </div>
                  <h2 className="text-3xl font-display font-bold text-slate-900">{currentPlan}</h2>
                  <p className="text-sm text-slate-500 mt-2">Currently assigned to {profile.school_name || 'your account'}</p>
                </div>
              </div>

              {/* Balance & Payment Card */}
              <div className={`p-6 rounded-2xl border flex flex-col justify-between shadow-sm transition-colors ${balance > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                <div>
                  <div className={`flex items-center gap-2 font-medium mb-4 ${balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                    {balance > 0 ? <AlertCircle size={18} /> : <CheckCircle2 size={18} />}
                    {balance > 0 ? 'Outstanding Balance Due' : 'All Settled'}
                  </div>
                  <h2 className={`text-4xl font-display font-bold ${balance > 0 ? 'text-red-900' : 'text-emerald-900'}`}>
                    Rs. {balance > 0 ? balance.toLocaleString() : '0'}
                  </h2>
                </div>
                
                {balance > 0 && (
                  <form onSubmit={handlePayment} className="mt-6">
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        max={balance}
                        value={payAmount}
                        onChange={(e) => setPayAmount(e.target.value)}
                        placeholder="Amount (Rs.)"
                        className="flex-1 px-4 py-2 bg-white border border-red-200 rounded-lg outline-none focus:border-red-400"
                        required
                      />
                      <button 
                        type="submit" 
                        disabled={processing}
                        className="bg-red-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center min-w-[120px]"
                      >
                        {processing ? 'Processing...' : 'Pay Now'}
                      </button>
                    </div>
                    {success && <p className="text-sm text-emerald-600 mt-2">Payment recorded successfully!</p>}
                  </form>
                )}
              </div>
              
            </div>

            {/* Ledger Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
                <FileText size={18} className="text-slate-500" />
                <h3 className="font-semibold text-slate-800">Billing Ledger</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-100 bg-white">
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Date</th>
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Description</th>
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase text-right">Debit (Bill)</th>
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase text-right">Credit (Paid)</th>
                      <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {ledger.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-slate-500">No billing history found.</td>
                      </tr>
                    ) : (
                      ledger.map((tx, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-6 py-4 text-sm text-slate-600">{new Date(tx.date).toLocaleDateString()}</td>
                          <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                            {tx.type === 'Debit' ? tx.description : `Payment (${tx.payment_mode})`}
                          </td>
                          <td className="px-6 py-4 text-sm text-slate-600 text-right">{tx.type === 'Debit' ? tx.amount.toLocaleString() : '-'}</td>
                          <td className="px-6 py-4 text-sm text-slate-600 text-right">{tx.type === 'Credit' ? tx.amount_paid.toLocaleString() : '-'}</td>
                          <td className={`px-6 py-4 text-sm font-semibold text-right ${tx.balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                            {Math.abs(tx.balance).toLocaleString()} {tx.balance > 0 ? 'Dr' : 'Cr'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
